import { TitleCasePipe } from '@angular/common';
import { Component, DestroyRef, ViewChild, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  ServiceRequestDetailsResponse,
  ServiceRequestDocument,
  ServiceRequestService,
} from '@core/services/service-request';
import { UserService } from '@core/services/user';
import { Conversation } from '@features/service-request-history/components/conversation/conversation';
import { Gradientsegmentbar } from '@shared/components/gradientsegmentbar/gradientsegmentbar';
import { getStatusClass, getStatusDisplay } from '@shared/utilities/status.util';
import {
  MdbTabChange,
  MdbTabsComponent,
  MdbTabsModule,
} from 'mdb-angular-ui-kit/tabs';
import { MdbRippleModule } from 'mdb-angular-ui-kit/ripple';

type DetailsTab = 'summary' | 'conversations';
type AttachmentSort = 'uploadedBy' | 'fileName' | 'date' | 'size';
type SortDirection = 'asc' | 'desc';

@Component({
  selector: 'app-service-request-details',
  imports: [
    TitleCasePipe,
    RouterLink,
    MdbRippleModule,
    MdbTabsModule,
    Conversation,
    Gradientsegmentbar,
  ],
  templateUrl: './service-request-details.html',
  styleUrl: './service-request-details.scss',
})
export class ServiceRequestDetails {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly serviceRequestService = inject(ServiceRequestService);
  private readonly userService = inject(UserService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly tabNames: DetailsTab[] = ['summary', 'conversations'];
  private tabsComponent?: MdbTabsComponent;
  private serviceRequestId: number | null = null;
  private requestedTabIndex = 0;

  protected readonly details = signal<ServiceRequestDetailsResponse | null>(null);
  protected readonly isLoading = signal(true);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly attachmentFilter = signal('');
  protected readonly attachmentSort = signal<AttachmentSort>('date');
  protected readonly attachmentSortDirection = signal<SortDirection>('desc');
  protected readonly attachmentPage = signal(1);
  protected readonly attachmentPageSize = 5;
  protected readonly activeTabIndex = signal(0);

  protected readonly getStatusDisplay = getStatusDisplay;
  protected readonly getStatusClass = getStatusClass;

  protected readonly attachments = computed(() => {
    return this.details()?.serviceRequestDocuments ?? [];
  });

  protected readonly filteredAttachments = computed(() => {
    const term = this.attachmentFilter().trim().toLowerCase();
    const filtered = term ? this.attachments().filter((document) => {
      const searchableText = [
        document.originalFileName,
        document.fileExtension,
        document.description,
        this.getUploadSource(document),
        this.formatDate(document.createdDate),
      ].join(' ').toLowerCase();

      return searchableText.includes(term);
    }) : [...this.attachments()];

    const column = this.attachmentSort();
    const multiplier = this.attachmentSortDirection() === 'asc' ? 1 : -1;

    return filtered.sort((a, b) => {
      let comparison: number;

      switch (column) {
        case 'uploadedBy':
          comparison = this.getUploadSource(a).localeCompare(this.getUploadSource(b));
          break;
        case 'fileName':
          comparison = a.originalFileName.localeCompare(b.originalFileName, undefined, {
            numeric: true,
            sensitivity: 'base',
          });
          break;
        case 'size':
          comparison = a.fileSizeBytes - b.fileSizeBytes;
          break;
        case 'date':
        default:
          comparison = new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();
          break;
      }

      return comparison * multiplier;
    });
  });

  protected readonly attachmentPageCount = computed(() => {
    return Math.max(1, Math.ceil(this.filteredAttachments().length / this.attachmentPageSize));
  });

  protected readonly pagedAttachments = computed(() => {
    const page = Math.min(this.attachmentPage(), this.attachmentPageCount());
    const startIndex = (page - 1) * this.attachmentPageSize;
    return this.filteredAttachments().slice(startIndex, startIndex + this.attachmentPageSize);
  });

  protected readonly attachmentPageNumbers = computed(() => {
    const pageCount = this.attachmentPageCount();
    const visiblePageCount = Math.min(5, pageCount);
    const startPage = Math.max(
      1,
      Math.min(this.attachmentPage() - 2, pageCount - visiblePageCount + 1)
    );

    return Array.from({ length: visiblePageCount }, (_, index) => startPage + index);
  });

  @ViewChild('tabs')
  set tabs(component: MdbTabsComponent | undefined) {
    this.tabsComponent = component;
    if (component) {
      setTimeout(() => component.setActiveTab(this.requestedTabIndex));
    }
  }

  constructor() {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const id = Number(params.get('id'));
        if (!Number.isInteger(id) || id <= 0) {
          this.serviceRequestId = null;
          this.isLoading.set(false);
          this.errorMessage.set('The requested service request could not be found.');
          return;
        }

        this.serviceRequestId = id;
        this.loadDetails(true);
      });

    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const requestedTab = params.get('tab') as DetailsTab | null;
        const tabIndex = requestedTab ? this.tabNames.indexOf(requestedTab) : 0;
        this.requestedTabIndex = tabIndex >= 0 ? tabIndex : 0;
        this.activeTabIndex.set(this.requestedTabIndex);

        if (this.tabsComponent) {
          setTimeout(() => this.tabsComponent?.setActiveTab(this.requestedTabIndex));
        }
      });
  }

  protected onTabChange(event: MdbTabChange): void {
    this.activeTabIndex.set(event.index);
    const tab = this.tabNames[event.index] ?? 'summary';
    const currentTab = this.route.snapshot.queryParamMap.get('tab') ?? 'summary';
    if (currentTab === tab) return;

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tab === 'summary' ? null : tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  protected refreshDetails(): void {
    this.loadDetails(false);
  }

  protected retry(): void {
    this.loadDetails(true);
  }

  protected filterAttachments(event: Event): void {
    this.attachmentFilter.set((event.target as HTMLInputElement).value);
    this.attachmentPage.set(1);
  }

  protected changeAttachmentSort(event: Event): void {
    this.setAttachmentSort((event.target as HTMLSelectElement).value as AttachmentSort);
  }

  protected setAttachmentSort(column: AttachmentSort): void {
    if (this.attachmentSort() === column) {
      this.toggleAttachmentSortDirection();
      return;
    }

    this.attachmentSort.set(column);
    this.attachmentSortDirection.set(column === 'date' ? 'desc' : 'asc');
    this.attachmentPage.set(1);
  }

  protected toggleAttachmentSortDirection(): void {
    this.attachmentSortDirection.update((direction) => direction === 'asc' ? 'desc' : 'asc');
    this.attachmentPage.set(1);
  }

  protected setAttachmentPage(page: number): void {
    if (page < 1 || page > this.attachmentPageCount()) return;
    this.attachmentPage.set(page);
  }

  protected getAttachmentRangeStart(): number {
    if (this.filteredAttachments().length === 0) return 0;
    return (this.attachmentPage() - 1) * this.attachmentPageSize + 1;
  }

  protected getAttachmentRangeEnd(): number {
    return Math.min(
      this.attachmentPage() * this.attachmentPageSize,
      this.filteredAttachments().length
    );
  }

  protected getAttachmentAriaSort(column: AttachmentSort): 'ascending' | 'descending' | 'none' {
    if (this.attachmentSort() !== column) return 'none';
    return this.attachmentSortDirection() === 'asc' ? 'ascending' : 'descending';
  }

  protected getAttachmentSortIcon(column: AttachmentSort): string {
    if (this.attachmentSort() !== column) return 'fa-sort';
    return this.attachmentSortDirection() === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
  }

  protected formatServiceRequestId(id: number): string {
    return `SR-${id.toString().padStart(4, '0')}`;
  }

  protected formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A';

    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/Los_Angeles',
    });
  }

  protected formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  protected getUploadSource(document: ServiceRequestDocument): string {
    return document.uploadedByUserType.toLowerCase() === 'staff' ? 'Self-Help Team' : 'You';
  }

  protected getDocumentIcon(fileExtension: string): string {
    switch (fileExtension.toLowerCase().replace('.', '')) {
      case 'pdf':
        return 'fa-file-pdf';
      case 'doc':
      case 'docx':
        return 'fa-file-word';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return 'fa-file-image';
      default:
        return 'fa-file';
    }
  }

  protected getUserName(): string {
    const user = this.userService.getUserInfoFromStorage();
    return user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : 'N/A';
  }

  protected getUserEmail(): string {
    return this.userService.getUserInfoFromStorage()?.email || 'N/A';
  }

  protected getUserPhoneNumber(): string {
    return this.userService.getUserInfoFromStorage()?.phoneNumber || 'N/A';
  }

  private loadDetails(showPageLoader: boolean): void {
    if (!this.serviceRequestId) return;

    if (showPageLoader) this.isLoading.set(true);
    this.errorMessage.set(null);

    this.serviceRequestService.getServiceRequestDetails(this.serviceRequestId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (details) => {
          if (
            details.serviceRequest.assignedToUserId === null &&
            this.requestedTabIndex === this.tabNames.indexOf('conversations')
          ) {
            this.requestedTabIndex = 0;
            this.activeTabIndex.set(0);
            void this.router.navigate([], {
              relativeTo: this.route,
              queryParams: { tab: null },
              queryParamsHandling: 'merge',
              replaceUrl: true,
            });
          }

          this.details.set(details);
          this.attachmentPage.set(1);
          this.isLoading.set(false);
        },
        error: () => {
          if (showPageLoader) this.details.set(null);
          this.isLoading.set(false);
          this.errorMessage.set(
            'We could not load this service request. Please try again or return to your service requests.'
          );
        },
      });
  }
}
